import { useState } from 'react';
import toast from 'react-hot-toast';
import { DesktopTowerIcon, GithubLogoIcon, GitlabLogoIcon, RobotIcon, TrashIcon } from '@phosphor-icons/react';

import { ProviderCard } from '@/features/settings/components/ProviderCard';
import { SettingsSection } from '@/features/settings/components/SettingsSection';
import { StatusPill } from '@/features/settings/components/StatusPill';
import { Button, LinkButton } from '@/ui/button/Button';
import { useUserSessions, revokeSession, type UserSession } from '@/api-models/user-sessions';
import { useOAuthAccounts, type OAuthAccountSummary } from '@/api-models/user-oauth-accounts';
import { useAgentTokens, revokeAgentToken, type AgentToken } from '@/api-models/agent-tokens';
import { formatTimeAgo } from '@/utils/format';

export function SecuritySection() {
  return (
    <>
      <ConnectedAccountsSection />
      <ActiveSessionsSection />
      <AgentTokensSection />
      <DeleteAccountSection />
    </>
  );
}

function ConnectedAccountsSection() {
  const { accounts, isLoading } = useOAuthAccounts();

  return (
    <SettingsSection title="Connected accounts" description="Sign-in providers linked to your account.">
      <div className="flex flex-col gap-2.5">
        {isLoading && <div className="text-xs text-fg-tertiary">Loading…</div>}
        {!isLoading && accounts.length === 0 && (
          <div className="rounded-lg border border-dashed border-line bg-surface-2/40 p-3.5 text-xs text-fg-tertiary">
            No sign-in providers linked yet.
          </div>
        )}
        {!isLoading &&
          accounts.map((account, idx) => (
            <ConnectedAccountCard key={`${account.provider}-${account.providerUsername ?? idx}`} account={account} />
          ))}
      </div>
    </SettingsSection>
  );
}

function ConnectedAccountCard({ account }: { account: OAuthAccountSummary }) {
  const isGitlab = account.provider === 'gitlab';
  const Icon = isGitlab ? GitlabLogoIcon : GithubLogoIcon;
  const providerLabel = isGitlab ? 'GitLab' : 'GitHub';
  const detail = account.providerUsername ? `@${account.providerUsername}` : (account.providerEmail ?? '');

  return (
    <ProviderCard
      icon={<Icon size={22} weight="duotone" className="text-fg" />}
      name={providerLabel}
      badge={<StatusPill tone="success">Connected</StatusPill>}
      description={[detail, `Connected ${formatTimeAgo(account.createdAt)}`].filter(Boolean).join(' · ')}
    />
  );
}

function ActiveSessionsSection() {
  const { sessions, mutate, isLoading } = useUserSessions();
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const handleRevoke = async (session: UserSession) => {
    setRevokingId(session.id);
    try {
      const result = await revokeSession(session.id);
      if (result.currentSession) {
        window.location.href = '/login';
        return;
      }
      await mutate();
      toast.success('Session signed out');
    } catch {
      toast.error('Failed to sign out session');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <SettingsSection
      title="Active sessions"
      description="Devices signed in to your account. Signing out of this device ends your current session."
    >
      <div className="flex flex-col gap-2.5">
        {isLoading && <div className="text-xs text-fg-tertiary">Loading…</div>}
        {!isLoading && sessions.length === 0 && (
          <div className="rounded-lg border border-dashed border-line bg-surface-2/40 p-3.5 text-xs text-fg-tertiary">
            No active sessions.
          </div>
        )}
        {!isLoading &&
          sessions.map((session) => (
            <ProviderCard
              key={session.id}
              icon={<DesktopTowerIcon size={22} weight="duotone" className="text-fg" />}
              name={session.current ? 'This device' : 'Session'}
              badge={
                session.current ? (
                  <StatusPill tone="brand" dot={false}>
                    Current
                  </StatusPill>
                ) : (
                  <StatusPill tone="success">Active</StatusPill>
                )
              }
              description={`Started ${formatTimeAgo(session.createdAt)} · Last active ${formatTimeAgo(
                session.updatedAt,
              )} · Expires ${new Date(session.expiresAt).toLocaleDateString()}`}
              actions={
                <Button
                  variant={session.current ? 'destructive' : 'secondary'}
                  size={8}
                  onClick={() => handleRevoke(session)}
                  disabled={revokingId === session.id}
                >
                  {session.current ? 'Sign out' : 'Revoke'}
                </Button>
              }
            />
          ))}
      </div>
    </SettingsSection>
  );
}

function AgentTokensSection() {
  const { tokens, mutate, isLoading } = useAgentTokens();
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const handleRevoke = async (token: AgentToken) => {
    setRevokingId(token.id);
    try {
      await revokeAgentToken(token.id);
      await mutate();
      toast.success('Agent token revoked');
    } catch {
      toast.error('Failed to revoke agent token');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <SettingsSection
      title="Agent tokens"
      description="Tokens your coding agents use. CLI tokens reach the model gateway only; full-access tokens can call the API as you."
      action={
        <LinkButton to="/get-token" variant="secondary" size={8} target="_blank">
          Get token
        </LinkButton>
      }
    >
      <div className="flex flex-col gap-2.5">
        {isLoading && <div className="text-xs text-fg-tertiary">Loading…</div>}
        {!isLoading && tokens.length === 0 && (
          <div className="rounded-lg border border-dashed border-line bg-surface-2/40 p-3.5 text-xs text-fg-tertiary">
            No active agent tokens. Create one with “Get token”.
          </div>
        )}
        {!isLoading &&
          tokens.map((token) => (
            <ProviderCard
              key={token.id}
              icon={<RobotIcon size={22} weight="duotone" className="text-fg" />}
              name={`${token.tokenPrefix}…`}
              badge={
                <StatusPill tone={token.scope === 'cli' ? 'brand' : 'success'} dot={false}>
                  {token.scope === 'cli' ? 'CLI' : 'Full access'}
                </StatusPill>
              }
              description={[
                token.label,
                token.lastUsedAt ? `Last used ${formatTimeAgo(token.lastUsedAt)}` : 'Never used',
                `Expires ${new Date(token.expiresAt).toLocaleDateString()}`,
              ]
                .filter(Boolean)
                .join(' · ')}
              actions={
                <Button
                  variant="secondary"
                  size={8}
                  onClick={() => handleRevoke(token)}
                  disabled={revokingId === token.id}
                >
                  Revoke
                </Button>
              }
            />
          ))}
      </div>
    </SettingsSection>
  );
}

function DeleteAccountSection() {
  return (
    <SettingsSection title="Danger zone" description="Irreversible actions on your account.">
      <div className="flex items-center gap-3.5 rounded-md border border-danger/25 bg-surface-2 p-3.5">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-tile bg-danger-tint text-danger">
          <TrashIcon size={20} weight="duotone" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-semibold text-fg">Delete account</span>
          <span className="text-xs text-fg-tertiary">
            Permanently deletes your account, sandboxes, and stored credentials. This can’t be undone and isn’t
            self-serve yet — contact support to request deletion.
          </span>
        </div>
        <Button variant="destructive" size={9} disabled>
          Delete account
        </Button>
      </div>
    </SettingsSection>
  );
}
